/* eslint-disable ember/no-computed-properties-in-native-classes */
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import getChartColor from 'frontend-utils/utils/hotel-chart-color';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  COLUMN_PROPERTIES,
  DETAIL_VIEW_TABS,
  REFERENCE_PERIODS,
  SUBTYPE_DELIMITER,
  TYPE_ID_BEST_FLEX,
  TYPE_ID_DOW,
  TYPE_ID_LEADTIME,
  TYPE_ID_LOS,
  TYPE_ID_MEALTYPE,
  TYPE_ID_MEMBERSHIP,
  TYPE_ID_MOBILE,
  TYPE_ID_NON_REF,
  TYPE_ID_POS,
  TYPE_ID_POSITION,
  TYPE_ID_ROOMTYPE,
  TYPE_ID_SEMI_FLEX,
} from 'lumen/utils/constants/rate-strategy-constants';

// Mapping of rstype IDs that don't match with their route name
const TYPE_TO_DETAIL_ROUTE_MAPPING = {
  day_of_week: 'dow',
  best_flex: 'cancellation',
  semi_flex: 'cancellation',
  non_ref: 'cancellation',
};

const createIdComparator = (id) => (other) => id === other.id;

// Recursively looks up an option in a list of options, including nested options:
// findOptionById([{ id: 'a' }, { options: [{ id: 'b' }] }], 'b') => { id: 'b' }
function findOptionById(options, id) {
  for (let i = 0; i < options.length; i++) {
    const currentOption = options[i];
    if (currentOption.id === id) {
      return currentOption;
    }

    if (currentOption.options) {
      const foundOption = findOptionById(currentOption.options, id);
      if (foundOption) {
        return foundOption;
      }
    }
  }

  return null;
}

export default class HotelStrategyController extends Controller {
  queryParams = ['competitors'];

  @service access;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service router;
  @service rsSelections;
  @tracked competitors = [];

  @tracked referencePeriodCancellationPolicySection =
    DEFAULT_QP_VALUES.hotel.strategy.referencePeriod;
  @tracked referencePeriodStrategySection =
    DEFAULT_QP_VALUES.hotel.strategy.referencePeriod;

  fromDate = null;

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get competitorsPerCompset() {
    const competitorsPerCompset = [];

    const primaryCompsetOptions =
      this.hotelAndMarketService.primaryCompetitors.map(
        (primaryCompetitor) => ({
          id: primaryCompetitor.id,
          label: primaryCompetitor.name,
          unsafe_bulletColor: getChartColor(
            primaryCompetitor.id,
            this.primaryAndSecondaryCompetitors,
          ),
          isEqual: createIdComparator(primaryCompetitor.id),
        }),
      );

    competitorsPerCompset.push({
      groupName: this.intl.t('settings.competitors.primary_compset'),
      options: primaryCompsetOptions,
    });

    if (this.hasSecondaryCompset) {
      const secondaryCompsetOptions =
        this.hotelAndMarketService.secondaryCompetitors.map(
          (secondaryCompetitor) => ({
            id: secondaryCompetitor.id,
            label: secondaryCompetitor.name,
            unsafe_bulletColor: getChartColor(
              secondaryCompetitor.id,
              this.primaryAndSecondaryCompetitors,
            ),
            isEqual: createIdComparator(secondaryCompetitor.id),
          }),
        );

      competitorsPerCompset.push({
        id: 'secondary',
        groupName: this.intl.t('settings.competitors.secondary_compset'),
        options: secondaryCompsetOptions,
      });
    }

    return competitorsPerCompset;
  }

  get competitorsValue() {
    return this.competitors
      .map((competitorId) =>
        findOptionById(this.competitorsPerCompset, competitorId),
      )
      .filter(Boolean);
  }

  @computed('timezone')
  get formattedDataRangeText() {
    const todayMoment = this.hotelAndMarketService.getLocalTime();
    return `${todayMoment.format('D MMMM YYYY')} - ${todayMoment

      .add(90, 'day')
      .format(
        'D MMMM YYYY',
      )} (${this.intl.t('general.next_days', { count: 90 })})`;
  }

  get hasSecondaryCompset() {
    return this.hotelAndMarketService.hasSecondaryCompset;
  }

  get hasTypesData() {
    return !this.isLoading && this.typesData !== null;
  }

  get isLoading() {
    return this.model.typeData?.isRunning;
  }

  get primaryAndSecondaryCompetitors() {
    return this.hotelAndMarketService.primaryAndSecondaryCompetitors;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get referencePeriodKeys() {
    return [
      {
        id: REFERENCE_PERIODS.ONE_WEEK_AGO,
        label: this.intl.t('general.versus.option', {
          option: this.intl.t('general.weeksago', { count: 1 }),
        }),
      },
      {
        id: REFERENCE_PERIODS.ONE_MONTH_AGO,
        label: this.intl.t('general.versus.option', {
          option: this.intl.t('general.monthsago', { count: 1 }),
        }),
      },
      {
        id: REFERENCE_PERIODS.THREE_MONTHS_AGO,
        label: this.intl.t('general.versus.option', {
          option: this.intl.t('general.monthsago', { count: 3 }),
        }),
      },
    ];
  }

  get selectedReferencePeriodForCancellationPolicySection() {
    return this.referencePeriodKeys.find(
      (referencePeriodKey) =>
        referencePeriodKey.id === this.referencePeriodCancellationPolicySection,
    );
  }

  get selectedReferencePeriodForStrategySection() {
    return this.referencePeriodKeys.find(
      (referencePeriodKey) =>
        referencePeriodKey.id === this.referencePeriodStrategySection,
    );
  }

  get showFullPageEmptyState() {
    if (!this.isLoading && this.typesData) {
      return this.typesData.every((type) => type.isMissing);
    }

    return false;
  }

  get subtypesWithStrategy() {
    const subtypesWithStrategy = [];

    if (this.typesData) {
      this.typesData.forEach((type) => {
        type.filteredSubtypes.forEach((subtype) => {
          if (
            subtype.hasStrategySubtypePosition &&
            !subtype.isNotOffered &&
            !subtype.isExcludedFromSummaryView
          ) {
            subtypesWithStrategy.push(subtype);
          }
        });
      });
    }

    return subtypesWithStrategy;
  }

  @computed('hotelAndMarketService.timezone')
  get timezone() {
    return this.hotelAndMarketService.timezone;
  }

  @computed('model.typeData.value')
  get typesData() {
    return this.model?.typeData?.value;
  }

  get typesDataPerId() {
    const result = {};

    if (this.typesData) {
      this.typesData.forEach((rstype) => {
        result[rstype.name] = rstype;
      });
    }

    return result;
  }

  static getRouteNameByTypeId(typeId) {
    // Use route name from Mapping constant if present, use ID directly as route name otherwise
    return TYPE_TO_DETAIL_ROUTE_MAPPING[typeId] ?? typeId;
  }

  @action
  competitorsBuildLabelWithCount(count) {
    return this.intl.t('general.competitors.count', { count });
  }

  @action
  handleCompetitorsChange(options) {
    this.competitors = options.map((option) => option.id);
    this.rsSelections.setSelectedCompetitorIDs(this.competitors);
  }

  @action
  initRsSelections() {
    this.rsSelections.setSelectedCompetitorIDs(this.competitors);
    this.rsSelections.referencePeriodStrategySection =
      this.referencePeriodStrategySection;
    this.rsSelections.referencePeriodCancellationPolicySection =
      this.referencePeriodCancellationPolicySection;
  }

  // NOTE: Rate strategy redirect behavior is quite complicated.
  @action
  redirectToPosSettings() {
    // Double check if the user has the right feature flags
    if (this.access.hasFeature('rate_strategy.pos.edit')) {
      this.router.transitionTo('hotel.settings.pos');
    }
  }

  // Expected behavior can be found in the specs document: https://docs.google.com/document/d/1jhgazIAKAPpRChuw2bNoVIFjF7_fKDoQPkJTCEVPhHk/edit?pli=1#
  @action
  switchToDetailViaCompsetPosPerStrategy(subtypeClicked) {
    if (!subtypeClicked) {
      // We don't pass a subtype for the position detail
      this.router.transitionTo('hotel.strategy.position', {
        queryParams: {
          sortBy: `${TYPE_ID_POSITION}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.RATE}`,
          sortDirection: 'desc',
        },
      });
    } else {
      const typeIdClicked = subtypeClicked.rstype.get('name');
      const subtypeIdClicked = subtypeClicked.id;

      const subRoute =
        HotelStrategyController.getRouteNameByTypeId(typeIdClicked);
      this.router.transitionTo(`hotel.strategy.${subRoute}`, {
        queryParams: {
          sortBy: `${subtypeIdClicked}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.RATE}`,
          sortDirection: 'desc',
        },
      });
    }
  }

  @action
  switchToDetailViaPositionButton(subtypeClicked) {
    const params = {
      queryParams: {
        sortDirection: 'desc',
      },
    };

    const typeIdClicked = subtypeClicked.rstype.get('name');
    const subtypeIdClicked = subtypeClicked.id;
    params.queryParams.tab = DETAIL_VIEW_TABS.TABLE;

    if (TYPE_TO_DETAIL_ROUTE_MAPPING[typeIdClicked] !== 'cancellation') {
      if (subtypeIdClicked) {
        // should presort on price of the subtype
        params.queryParams.sortBy = `${subtypeIdClicked}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.RATE}`;
      }
    } else {
      params.queryParams.sortBy = `${typeIdClicked}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.RATE}`;
    }

    const subRoute =
      HotelStrategyController.getRouteNameByTypeId(typeIdClicked);
    this.router.transitionTo(`hotel.strategy.${subRoute}`, params);
  }

  @action
  switchToDetailViaTitleExpand(typeClicked) {
    const params = {
      queryParams: {
        sortDirection: 'desc',
      },
    };

    // If a type is missing, we can't show much in the detail views, so disable this for now
    if (this.typesDataPerId[typeClicked].isMissing) {
      return;
    }

    // Day of week, Leadtime, Roomtype have presorting on hotel (handled by default URL parameters)
    if (
      typeClicked === TYPE_ID_DOW ||
      typeClicked === TYPE_ID_LEADTIME ||
      typeClicked === TYPE_ID_ROOMTYPE
    ) {
      params.queryParams.tab = DETAIL_VIEW_TABS.GRAPH; // Open on graph tab by default
    }

    // Exceptions for other cases:
    // -> need to sort on discount column of first available subtype
    if (
      typeClicked === TYPE_ID_LOS ||
      typeClicked === TYPE_ID_MOBILE ||
      typeClicked === TYPE_ID_MEMBERSHIP ||
      typeClicked === TYPE_ID_POS
    ) {
      params.queryParams.sortBy = `${this.typesDataPerId[typeClicked].filteredSubtypes[0].id}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.DISCOUNT}`;
    }
    if (typeClicked === TYPE_ID_MEALTYPE) {
      params.queryParams.sortBy = `${COLUMN_PROPERTIES.ROOM_ONLY}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.RATE}`;
    }

    // -> need to sort on certain column depending on which cancellation type the user has clicked
    if (typeClicked === TYPE_ID_BEST_FLEX) {
      params.queryParams.sortBy = `${typeClicked}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.CUT_OFF_POLICY}`;
    }
    if (typeClicked === TYPE_ID_SEMI_FLEX || typeClicked === TYPE_ID_NON_REF) {
      params.queryParams.sortBy = `${typeClicked}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.DISCOUNT}`;
    }

    const subRoute = HotelStrategyController.getRouteNameByTypeId(typeClicked);
    this.router.transitionTo(`hotel.strategy.${subRoute}`, params);
  }

  @action
  updateReferencePeriodForCancellationPolicySection(referencePeriod) {
    this.referencePeriodCancellationPolicySection = referencePeriod.id;
    this.rsSelections.referencePeriodCancellationPolicySection =
      referencePeriod.id;
  }

  @action
  updateReferencePeriodForStrategySection(referencePeriod) {
    this.referencePeriodStrategySection = referencePeriod.id;
    this.rsSelections.referencePeriodStrategySection = referencePeriod.id;
  }
}
